import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { IncidentsApiAdapter } from '../adapters/IncidentsApiAdapter';
import { IncidentFilters } from '../models/Filters';
import { Service } from '../services/Service';
import {
  SolveIncidentPayload,
  ManagementPayload,
  CourierResponsePayload,
  ResolveByCourierPayload,
} from '../models/Incident';

export interface AdvancedFilter {
  column: string;
  operator: string;
  value: string;
}

export interface UseIncidentsFilters extends IncidentFilters {
  advanced_filters?: {
    filters: AdvancedFilter[];
    logic: 'AND' | 'OR';
  };
}

/**
 * Hook para obtener el listado de incidencias con paginación infinita
 */
export const useIncidents = (filters: UseIncidentsFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['incidents', filters],
      queryFn: ({ pageParam = 1 }) => {
        return service.getPaginated({
          ...filters,
          page: pageParam,
        });
      },
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage?.meta?.currentPage ?? 1;
        const lastPageNum = lastPage?.meta?.lastPage ?? 1;
        return currentPage < lastPageNum ? currentPage + 1 : undefined;
      },
      initialPageParam: 1,
      staleTime: 1 * 60 * 1000, // 1 minuto
      gcTime: 5 * 60 * 1000, // 5 minutos
      placeholderData: (previousData) => previousData,
    });

  const incidents = useMemo(() => data?.pages.flatMap((page) => page?.data ?? []) ?? [], [data?.pages]);

  // Obtener el total del API (siempre será el mismo en todas las páginas)
  const totalFromApi = data?.pages?.[0]?.meta?.total ?? 0;

  return {
    incidents,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total: totalFromApi,
  };
};

/**
 * Hook para obtener el detalle de una incidencia
 */
export const useIncident = (id: number | null) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useQuery({
    queryKey: ['incident', id],
    queryFn: () => apiAdapter.getIncidentById(id!),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para obtener las acciones elegibles de una incidencia
 */
export const useIncidentActions = (id: number | null) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useQuery({
    queryKey: ['incident-actions', id],
    queryFn: () => apiAdapter.getIncidentActions(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos (cambian poco)
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para obtener el historial de una incidencia
 */
export const useIncidentHistory = (id: number | null) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useQuery({
    queryKey: ['incident-history', id],
    queryFn: () => apiAdapter.getIncidentHistory(id!),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para resolver una incidencia
 */
export const useSolveIncident = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useMutation({
    mutationFn: ({
      id,
      payload,
      idempotencyKey,
    }: {
      id: number;
      payload: SolveIncidentPayload;
      idempotencyKey: string;
    }) => apiAdapter.solveIncident(id, payload, idempotencyKey),
    onSuccess: (_, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['incident', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incidents-count-by-status'] });
      queryClient.invalidateQueries({
        queryKey: ['incident-history', variables.id],
      });
    },
  });
};

/**
 * Hook para marcar una incidencia en gestión manual
 */
export const useMarkInManagement = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ManagementPayload }) =>
      apiAdapter.markInManagement(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incidents-count-by-status'] });
    },
  });
};

/**
 * Hook para registrar respuesta del courier
 */
export const useRegisterCourierResponse = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CourierResponsePayload }) =>
      apiAdapter.registerCourierResponse(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incidents-count-by-status'] });
    },
  });
};

/**
 * Hook para resolver por courier (atajo)
 */
export const useResolveByCourier = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new IncidentsApiAdapter(httpClient);

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ResolveByCourierPayload }) =>
      apiAdapter.resolveByCourier(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['incident', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incidents-count-by-status'] });
    },
  });
};
